/* eslint-disable react/no-array-index-key */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';
import color from 'color';

const COLOR_SCHEMA = {
    standard: {
        bgr: props => {
            return props.theme.content.backgroundColor;
        },
        label: props => {
            return props.theme.color;
        },
        chart: props => {
            return props.theme.radar.strokeColorHome;
        },
        chartFill: props => {
            return props.theme.radar.fillColorHome;
        },
    },
    statsTable: {
        bgr: props => {
            return color(props.theme.primaryColor)
                .alpha(0.5)
                .rgb()
                .toString();
        },
        label: props => {
            return props.theme.labelTextColor;
        },
        chart: props => {
            return props.theme.radar.strokeColorHome;
        },
        chartFill: props => {
            return props.theme.radar.fillColorHome;
        },
    },
};

const colorsFor = (props, colorFor) => {
    const set = _.get(COLOR_SCHEMA, `${props.colorScheme}`, COLOR_SCHEMA.standard);
    return set[colorFor] ? set[colorFor](props) : '#fff';
};

const RadarChart = styled.div`
    text-align: center;
    overflow: visible !important;

    svg {
        width: 100%;
        overflow: visible;
        padding: 10px 0 0;
        //hyphens: auto;

        .grid {
            polygon:nth-child(1) {
                stroke: transparent;
                fill: ${props => props.theme.radar.polygonBgColor};
            }
        }
    }

    .dashed-circle {
        stroke-dasharray: 5px;
        stroke: currentcolor;
        fill: none;
    }

    .circle {
        fill: #fff;

        &__chart-opponent1 {
            fill: ${props => colorsFor(props, 'chart')};
        }

        &__chart-opponent2 {
            fill: ${props => props.theme.radar.strokeColorAway};
        }

        &__chart-opponent3 {
            fill: ${props => props.theme.radar.strokeColorOpponent};
        }
    }

    line {
        stroke: ${props => props.theme.radar.polygonBorderColor};
    }

    .label {
        fill: ${props => colorsFor(props, 'label')};
        font-size: 20px;
        font-weight: ${({ theme }) => theme.fonts.main.bold || 'bold'};

        &__opponent1 {
            fill: ${props => colorsFor(props, 'chart')};
            text-transform: none;
        }

        &__opponent2 {
            fill: ${props => props.theme.radar.strokeColorAway};
            text-transform: none;
        }

        &__opponent3 {
            fill: ${props => props.theme.radar.strokeColorOpponent};
            text-transform: none;
        }
    }
`;

const Polygon = styled.polygon`
    fill: none;
    stroke-width: 1;
    stroke: ${props => props.theme.radar.polygonBorderColor};

    &.border-none {
        stroke: none;
    }

    &.polygon__chart-opponent1,
    &.polygon__chart-opponent2,
    &.polygon__chart-opponent3 {
        stroke-width: ${({ lineWidth }) => lineWidth || 2};
    }

    &.polygon__chart-opponent1 {
        stroke: ${props => colorsFor(props, 'chart')};
        fill: ${props =>
            color(colorsFor(props, 'chartFill'))
                .rgb()
                .toString()};
    }

    &.polygon__chart-opponent2 {
        stroke: ${props => props.theme.radar.strokeColorAway};
        fill: ${props =>
            color(props.theme.radar.fillColorAway)
                .rgb()
                .toString()};
    }

    &.polygon__chart-opponent3 {
        stroke: ${props => props.theme.radar.strokeColorOpponent};
        fill: ${props =>
            color(props.theme.radar.fillColorOpponent)
                .rgb()
                .toString()};
    }
`;

const calcPolygonPoints = (dotSize, diameter, sides, radiuses) => {
    const RAD = Math.PI / 180;
    const TWO_PI = Math.PI * 2;

    const center = diameter / 2;
    const theta = TWO_PI / sides;
    const angle = RAD * -90;

    const points = radiuses.map(radius => {
        const pointArr = [];

        for (let i = 0; i < sides; i++) {
            const x = radius * Math.cos(theta * i + angle) + center;
            const y = radius * Math.sin(theta * i + angle) + center;
            pointArr.push([x, y]);
        }

        return pointArr;
    });

    return points;
};

const getRadiuses = (diameter, level) => {
    let radius = diameter / 2;
    const distance = diameter / (2 * level);
    let r = 0;
    const radiuses = [];

    for (var i = 0; i < level; i++) {
        r = radius;
        radius -= distance;
        radiuses.push(r);
    }

    return radiuses;
};

class SVGRadarChart extends Component {
    static defaultProps = {
        diameter: 300,
        level: 5,
        colorScheme: 'standard',
        isScaleLines: true,
        isDashedCircle: false,
        labelWidth: 230,
    };

    constructor(props, context) {
        super(props, context);

        const sides = props.stats.length;
        this.state = {
            polygons: calcPolygonPoints(props.dotSize, props.diameter, sides, getRadiuses(props.diameter, props.level)),
        };
    }

    componentWillReceiveProps(nextProps) {
        const { diameter, level, dotSize } = this.props;
        const { stats } = nextProps;
        const sides = stats.length;

        const polygons = calcPolygonPoints(dotSize, diameter, sides, getRadiuses(diameter, level));

        this.setState({
            polygons,
        });
    }

    getDotOnChart(stat, i) {
        const { stats, diameter } = this.props;
        const sides = stats.length;
        const RAD = Math.PI / 180;
        const TWO_PI = Math.PI * 2;

        const center = diameter / 2;
        const theta = TWO_PI / sides;
        const angle = RAD * -90;

        if (!stat.max) stat.max = center;

        const radius = (stat.value / stat.max / 2) * diameter;

        const x = radius * Math.cos(theta * i + angle) + center;
        const y = radius * Math.sin(theta * i + angle) + center;

        return { x, y };
    }

    getLabelAlignType() {
        const { stats, labelWidth, diameter } = this.props;
        const sides = stats.length;

        switch (sides) {
            case 3:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
            case 4:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: diameter / 2,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
            case 5:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
            case 6:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
            case 7:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
            case 8:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
            case 9:
            default:
                return [
                    {
                        tAlign: 'center',
                        textAnchor: 'middle',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'left',
                        textAnchor: 'start',
                        offset: 0,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                    {
                        tAlign: 'right',
                        textAnchor: 'end',
                        offset: labelWidth,
                    },
                ];
        }
    }

    renderLabels() {
        const { stats, hideStatLabels } = this.props;
        const opponents = Object.keys(stats[0]).filter(o => o.includes('opponent'));

        return stats.map((stat, index) => {
            const labelOutOfChart = this.getDotOnChart({ value: (stat.max || 10) * 1.2, max: stat.max || 10 }, index);

            const text = !hideStatLabels ? (
                <text
                    key={stat.label}
                    className="label"
                    x={labelOutOfChart.x}
                    y={labelOutOfChart.y}
                    dy={-3}
                    textAnchor={this.getLabelAlignType()[index].textAnchor || 'middle'}
                >
                    {opponents.map((opponent, i) => {
                        const opponentStat = Math.round(stats[index][opponent] * 10) / 10;
                        const isLast = opponents.length - 1 === i;

                        return [
                            <tspan key="label" className={`label label__${opponent}`}>
                                {opponentStat}
                            </tspan>,
                            <tspan key="symbol" className="label label__symbol">
                                {!isLast ? ' / ' : ''}
                            </tspan>,
                        ];
                    })}
                </text>
            ) : null;

            return [
                text,
                <foreignObject
                    key="label"
                    x={index ? labelOutOfChart.x - this.getLabelAlignType()[index].offset : 0}
                    y={labelOutOfChart.y}
                    width={this.getLabelAlignType()[index].tAlign === 'center' ? '100%' : this.props.labelWidth}
                    height={90}
                    style={{ textAlign: this.getLabelAlignType()[index].tAlign || 'center', fontWeight: 400 }}
                    className="label"
                >
                    <span>
                        {stat.label} {stat.postfix ? `(${stat.postfix})` : ''}
                    </span>
                </foreignObject>,
            ];
        });
    }

    getPath(opponent) {
        const { stats } = this.props;

        return stats.reduce((accum, curr, index) => {
            const coordinates = this.getDotOnChart({ value: curr[opponent] || 0, max: curr.max }, index);
            return [...accum, [coordinates.x, coordinates.y]];
        }, []);
    }

    renderChart() {
        const { stats, dotSize = 4, lineWidth } = this.props;
        const opponents = Object.keys(stats[0]).filter(o => o.includes('opponent'));

        return (
            <g className="chart">
                {opponents.map(opponent => {
                    return (
                        <g className="polygon" key={opponent}>
                            <Polygon
                                className={`polygon polygon__chart-${opponent}`}
                                points={this.getPath(opponent).join(' ')}
                                lineWidth={lineWidth}
                            />
                            {this.getPath(opponent).map(p => {
                                const x = p[0];
                                const y = p[1];

                                return (
                                    <circle
                                        key={opponent + x + y}
                                        cx={x}
                                        cy={y}
                                        r={dotSize}
                                        className={`circle circle__chart-${opponent}`}
                                    />
                                );
                            })}
                        </g>
                    );
                })}

                <g className="labels">{this.renderLabels()}</g>
            </g>
        );
    }

    renderSvg() {
        const { polygons } = this.state;
        const { diameter } = this.props;
        const offset = 50; //for padding top

        if (!polygons.length) return;

        return (
            <svg viewBox={`0 -${offset} ${diameter} ${diameter + offset * 2}`} height={this.props.chartHeight}>
                <g className="grid">
                    {polygons.map((points, i) => {
                        const pointValues = points.join(' ');
                        return (
                            <Polygon
                                key={i}
                                className={this.props.isScaleLines ? 'polygon' : 'polygon border-none'}
                                points={pointValues}
                            />
                        );
                    })}
                </g>
                {this.renderChart()}
            </svg>
        );
    }

    render() {
        return <RadarChart colorScheme={this.props.colorScheme}>{this.renderSvg()}</RadarChart>;
    }
}

SVGRadarChart.contextTypes = {
    info: PropTypes.object,
};

export default SVGRadarChart;
export { SVGRadarChart };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/views/SVGRadarChart.js
 **/